import type { DomRaw } from "../index.js";
import type { NexusHandler } from "../../nexus/function/function.types";
import type { Unset } from "../../event/head.js";
import { unsetAll } from "../../../exports/event.js";

const noop = (): void => {};

/**
 *
 */

export interface HooksOptions {
  /**
 *
 *
 *
 *
 *
 */
  once?: boolean;

  /**
 *
 *
 *
 */
  signal?: AbortSignal;
}

/**
 *
 */
export interface OnMountHooksOptions extends HooksOptions {
  /**
 *
 *
 *
 *
 *
 */
  skipExisting?: boolean;
}

/**
 *
 */
export interface OnUnmountHooksOptions extends HooksOptions {}

/**
 *
 *
 *
 *
 *
 */
export type OnMountCallback<E> = (element: E) => void | Unset | Promise<void | Unset>;

/**
 *
 *
 *
 *
 */
export type OnUnmountCallback<E> = (element: E) => void | Promise<void>;

/**
 *
 */
type MountingHookOptions = [selectorOrElement: string | Element, once: boolean];

/**
 *
 */
const onMountListeners = new Map<OnMountCallback<Element>, MountingHookOptions>();
/**
 *
 */
const onUnmountListeners = new Map<OnUnmountCallback<Element>, MountingHookOptions>();

/**
 *
 *
 */
const elementsToUnsetHooks = new WeakMap<Element, Set<Unset>>();

/**
 *
 *
 */
const onMountToUnsetHooks = new WeakMap<OnMountCallback<Element>, Set<[Element, Unset]>>();

/**
 *
 *
 *
 *
 *
 *
 */
export function subscribeToAbortSignalIfAny(
  removeListener: () => void,
  signal?: AbortSignal,
): Unset {
  /*                 */
  if (!signal) return noop;

  /*                                                                            */
  /*                                                 */
  if (signal.aborted) {
    removeListener();
    return noop;
  }

  /*                                                                        */
  /*                                                                  */
  signal.addEventListener("abort", removeListener);
  return () => signal.removeEventListener("abort", removeListener);
}

/**
 *
 *
 *
 *
 *
 *
 */
export async function invokeCallbackWithOptions(
  callback: OnMountCallback<Element>,
  callbackToOptions: Map<OnMountCallback<Element>, MountingHookOptions>,
  once: boolean,
  node: Element,
): Promise<void> {
  const unset = await callback(node);
  if (once) callbackToOptions.delete(callback);

  /*                                                   */
  /*                                                                       */
  if (!unset) return;

  /*                                      */
  /*                                                     */
  if (!elementsToUnsetHooks.has(node)) elementsToUnsetHooks.set(node, new Set());
  if (!onMountToUnsetHooks.has(callback)) onMountToUnsetHooks.set(callback, new Set());

  /*                                                       */
  elementsToUnsetHooks.get(node)!.add(unset);
  onMountToUnsetHooks.get(callback)!.add([node, unset]);
}

/**
 *
 *
 *
 *
 */
export function removeUnsetHooksForListener(callback: OnMountCallback<Element>): void {
  const nodeToUnset = onMountToUnsetHooks.get(callback);
  if (!nodeToUnset) return;

  /*                                             */
  nodeToUnset.forEach(([node, unset]) => {
    const unsets = elementsToUnsetHooks.get(node);
    if (!unsets) return;
    unsets.delete(unset);
  });

  /*                                   */
  onMountToUnsetHooks.delete(callback);
}

/**
 *
 *
 */
const ElementClass = window.Element;

/**
 *
 */
export const eventTraget = new EventTarget();
/*                                                            */
const Event = CustomEvent;

/*                                             */
const invocationEventName = "on-hook-invoke";
const dispatchInvocationEvent = (): void => {
  eventTraget.dispatchEvent(new Event(invocationEventName));
};
const addInvocationListener = (callback: () => void): void => {
  eventTraget.addEventListener(invocationEventName, callback, { once: true });
};

const processForSelector = (
  node: Element,
  selectorOrElement: string,
  listener: OnMountCallback<Element>,
  map: Map<OnMountCallback<Element>, MountingHookOptions>,
  once: boolean,
): void => {
  if (node.matches(selectorOrElement))
    addInvocationListener(() => invokeCallbackWithOptions(listener, map, once, node));

  /*                                                                                                                     */
  node.querySelectorAll(selectorOrElement).forEach((child) => {
    addInvocationListener(() => invokeCallbackWithOptions(listener, map, once, child));
  });
};

const processForElement = (
  node: Element,
  selectorOrElement: Element,
  listener: OnMountCallback<Element>,
  map: Map<OnMountCallback<Element>, MountingHookOptions>,
  once: boolean,
): void => {
  /*                                                                                    */
  if (node === selectorOrElement)
    addInvocationListener(() => invokeCallbackWithOptions(listener, map, once, node));
  /*                                                                                          */
  else if (node.contains(selectorOrElement))
    addInvocationListener(() => invokeCallbackWithOptions(listener, map, once, selectorOrElement));
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function invokeListenersForNodeIfAny(
  this: [
    map: Map<OnMountCallback<Element>, MountingHookOptions>,
    entries: [OnMountCallback<Element>, MountingHookOptions][],
  ],
  node: Node,
): void {
  if (!(node instanceof Element)) return;
  const [map, entries] = this;

  entries.forEach(([listener, [selectorOrElement, once]]) => {
    /*                                                                                                   */
    if (typeof selectorOrElement !== "string") {
      processForElement(node as Element, selectorOrElement, listener, map, once);
    }
    /*                                                                                                                                      */
    else {
      processForSelector(node as Element, selectorOrElement, listener, map, once);
    }
  });

  dispatchInvocationEvent();
}

/**
 *
 *
 *
 *
 */
export function invokeUnsetsForElement(node: Node): void {
  if (!(node instanceof ElementClass)) return;

  /*                               */
  const unsets = elementsToUnsetHooks.get(node);
  if (!unsets) return;

  /*                                     */
  unsets.forEach(addInvocationListener);
  dispatchInvocationEvent();

  /*                                                                    */
  elementsToUnsetHooks.delete(node);
}

/**
 *
 *
 *
 *
 */
export function processMutation({ addedNodes, removedNodes }: MutationRecord): void {
  /*                                                                */
  const onMountEntries = Array.from(onMountListeners.entries());
  const onUnmountEntries = Array.from(onUnmountListeners.entries());

  /*                                                       */
  addedNodes.forEach(invokeListenersForNodeIfAny, [onMountListeners, onMountEntries]);
  removedNodes.forEach(invokeListenersForNodeIfAny, [onUnmountListeners, onUnmountEntries]);

  /*                                                                         */
  removedNodes.forEach(invokeUnsetsForElement);
}

/**
 *
 */
let observer: MutationObserver | null = null;

/**
 *
 *
 *
 *
 */
export function initializeMountingHooks(): Unset {
  /*                                                         */
  observer = new MutationObserver((mutations) => mutations.forEach(processMutation));
  observer.observe(document, { childList: true, subtree: true });

  /*                                */
  /*                 */
  return import.meta.env.DEV
    ? () => {
        if (observer) {
          observer.disconnect();
          observer = null;
          onMountListeners.clear();
          onUnmountListeners.clear();
        }
      }
    : noop;
  /*                */
}

/**
 *
 */
export const onMount: NexusHandler<DomRaw["onMount"]> = (
  selectorOrElement,
  callback,
  options = {},
) => {
  onMountListeners.set(callback, [selectorOrElement, !!options.once]);

  /*                                                                                                                                             */
  if (!options.skipExisting && typeof selectorOrElement === "string") {
    const invoke = invokeCallbackWithOptions.bind(null, callback, onMountListeners, !!options.once);
    document.querySelectorAll(selectorOrElement).forEach(invoke);
  }
  /*                                                                                                    */
  else if (
    !options.skipExisting &&
    selectorOrElement instanceof Element &&
    document.body.contains(selectorOrElement)
  ) {
    /*                                                                                                           */
    invokeCallbackWithOptions(callback, onMountListeners, !!options.once, selectorOrElement);
  }

  /*                                                 */
  /*                                     */
  const removeListener = (): void => {
    onMountListeners.delete(callback);
    removeUnsetHooksForListener(callback);
  };

  /*                                                  */
  return unsetAll(subscribeToAbortSignalIfAny(removeListener, options.signal), removeListener);
};

/**
 *
 */
export const onUnmount: NexusHandler<DomRaw["onUnmount"]> = (
  selectorOrElement,
  callback,
  options = {},
) => {
  onUnmountListeners.set(callback, [selectorOrElement, !!options.once]);

  const removeListener = (): void => {
    onUnmountListeners.delete(callback);
  };

  return unsetAll(subscribeToAbortSignalIfAny(removeListener, options.signal), removeListener);
};
